import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  MenuItem,
  FormControl,
  OutlinedInput,
  Select as MuiSelect,
  Box,
} from '@mui/material';
import { ModalShell } from '../ui/shell';
import { Btn, Field, Input, Select, Tag, FormGrid, MODAL_SCRIM_Z_INDEX } from '../ui/primitives';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import Toggle from '../ui/Toggle';
import { LabelWithHelp } from '../ui/FieldHelp';
import { TRAINING_DIALOG_FIELD_HELP as HELP } from '../ui/listScreenFieldHelp';
import { generateScript } from './generateScript';
import Promptdialog from './promptdialog';
import { GenerateQuestions } from '../assignment/generatequestions';
import axios from 'axios';
import { useCourseList } from '../hooks/useCourseList';
import { useAppContext } from '../../appprovider';
import {
  canUseAiCourseBuilder,
  canUseScormTraining,
  aiCourseBuilderGateBody,
  scormTrainingGateBody,
} from '../services/instructorMeterLimits';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const FormRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const GridField = styled(Field)`
  margin-bottom: 0;
  min-width: 0;
`;

const TwoColGrid = styled(FormGrid)`
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.rLg};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface2};
`;

const ToggleCopy = styled.div`
  min-width: 0;
`;

const ToggleTitle = styled.span`
  display: block;
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 650;
  color: ${({ theme }) => theme.text};
`;

const ToggleHint = styled.span`
  display: block;
  margin-top: 2px;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
  line-height: 1.4;
`;

const DisclaimerCard = styled.div`
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.rLg};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface2};
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const FooterWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
`;

const MuiSelectWrap = styled(FormControl)`
  width: 100%;
  min-width: 0;
`;

/** Returns MUI sx overrides so portaled multi-selects match portal theme tokens. */
function useMuiSelectSx() {
  const theme = useTheme();
  return {
    borderRadius: theme.rMd,
    backgroundColor: theme.surface2,
    color: theme.text,
    fontSize: '13.5px',
    minHeight: theme.controlH,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.borderStrong },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.accent,
      borderWidth: 1,
    },
    '&.Mui-focused': {
      backgroundColor: theme.surface,
      boxShadow: `0 0 0 3px ${theme.accentSoft}`,
    },
    '& .MuiSelect-icon': { color: theme.textMuted },
  };
}

const FormDialog = ({ open, handleClose, data, handleFormSubmit, loading }) => {
  const theme = useTheme();
  const muiSelectSx = useMuiSelectSx();
  const { courses, rawCourses, coursesLoading } = useCourseList();
  const { entitlements, openUpgradeGate } = useAppContext();
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const aiBuilderEnabled = canUseAiCourseBuilder(entitlements);
  const scormTrainingEnabled = canUseScormTraining(entitlements);

  /** Enables SCORM mode only when the org tier includes scorm_training. */
  const handleScormToggle = (enabled) => {
    if (enabled && !scormTrainingEnabled) {
      openUpgradeGate(scormTrainingGateBody(entitlements));
      return;
    }
    setFormData({ ...formData, isScrom: enabled ? 1 : 0 });
  };
  const [courseDetails, setCourseDetails] = useState({ courseId: '', vendor: '', domains: [] });
  const [script, setScript] = useState('');
  const [loading1, setLoading1] = useState(false);
  const [isPromptDialogOpen, setPromptDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    trainingName: '',
    trainingId: '',
    userId: localStorage.getItem('userId'),
    courseName: '',
    courseId: '',
    domains: [],
    startDate: '',
    completionDate: '',
    numberOfCharacters: 0,
    gender: '',
    race: '',
    language: '',
    videoLength: 0,
    questionsPerVideo: 0,
    createFinalExam: 0,
    active: 1,
    isScrom: 0,
    ...data,
  });
  const genderOptions = ['Male', 'Female'];
  const raceOptions = ['Asian', 'Black or African American', 'Hispanic or Latino', 'White', 'Other'];
  const languageOptions = ['English', 'Spanish', 'Chinese', 'Other'];

  useEffect(() => {
    if (data) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        trainingName: data.trainingName || '',
        trainingId: data.trainingId || '',
        userId: data.userId || localStorage.getItem('userId'),
        courseName: data.courseName || '',
        courseId: data.courseId || '',
        domains: data.domains || [],
        startDate: data.startDate ? new Date(parseInt(data.startDate)).toISOString().split('T')[0] : '',
        completionDate: data.completionDate ? new Date(parseInt(data.completionDate)).toISOString().split('T')[0] : '',
        numberOfCharacters: data.numberOfCharacters || 0,
        gender: data.gender || '',
        race: data.race || '',
        language: data.language || '',
        videoLength: data.videoLength || 0,
        questionsPerVideo: data.questionsPerVideo || 0,
        createFinalExam: data.createFinalExam || 0,
        active: data.active || 1,
        isScrom: data.isScrom || 0,
      }));
    }
  }, [data]);

  const apiUrl = process.env.REACT_APP_API_URL;
  const userId = localStorage.getItem('userId');

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  useEffect(() => {
    async function fetchPrompt() {
      try {
        const response = await axios.get(apiUrl + `/api/script/${formData.trainingId}`, {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            'Access-Control-Allow-Origin': '*',
            Authorization: getBasicAuthHeader(),
          },
        });
        if (response.data && response.data.script) {
          setScript(response.data.script);
        }
      } catch (error) {
        // legacy: script fetch errors are ignored
      }
    }

    if (open && formData.trainingId) {
      fetchPrompt();
    }
  }, [open, apiUrl, userId, formData.trainingId]);

  const handleCourseChange = (e) => {
    const selectedCourseName = e.target.value;
    const selectedCourse = rawCourses.find((course) => course.courseName === selectedCourseName);
    if (selectedCourse) {
      setCourseDetails({
        ...courseDetails,
        courseId: selectedCourse.courseId,
        vendor: selectedCourse.vendor,
        domains: selectedCourse.domains,
      });
      setFormData({
        ...formData,
        courseId: selectedCourse.courseId,
        courseName: selectedCourse.courseName,
        domains: [],
      });
    }
  };

  const handleDomainSelectChange = (event) => {
    const selectedDomainIds = event.target.value || [];
    const selectedDomains = courseDetails.domains.filter((domains) =>
      selectedDomainIds.includes(domains.domainId),
    );

    const updatedDomainData = selectedDomains.map((domains) => ({
      ...domains,
      subDomain: domains.subDomain,
    }));

    setFormData({
      ...formData,
      domains: updatedDomainData,
    });
  };

  const handleSubDomainSelectChange = (event) => {
    const selectedSubDomainIds = event.target.value || [];

    const updatedDomainData = formData.domains.map((domains) => ({
      ...domains,
      subDomain: domains.subDomain
        .map((subDomain) =>
          selectedSubDomainIds.includes(subDomain.subDomainId) ? subDomain : null,
        )
        .filter(Boolean),
    }));

    setFormData({
      ...formData,
      domains: updatedDomainData,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const convertDateToTimestamp = (date) => {
    if (date) {
      return new Date(date).getTime().toString();
    }
    return '';
  };

  const handleSubmit = () => {
    if (!guardAction('action.training.create.save')) return;
    const formattedData = {
      ...formData,
      startDate: convertDateToTimestamp(formData.startDate),
      completionDate: convertDateToTimestamp(formData.completionDate),
      numberOfCharacters: parseInt(formData.numberOfCharacters, 10),
      videoLength: parseInt(formData.videoLength, 10),
      questionsPerVideo: parseInt(formData.questionsPerVideo, 10),
      createFinalExam: formData.createFinalExam ? 1 : 0,
    };
    handleFormSubmit(formattedData);
  };

  const handleGenerateScript = () => {
    if (!guardAction('action.training.generateScript')) return;
    if (!aiBuilderEnabled) {
      openUpgradeGate(aiCourseBuilderGateBody(entitlements));
      return;
    }
    if (script) {
      setPromptDialogOpen(true);
    } else {
      handleGenerateScriptFlow();
    }
  };

  const handleGenerateScriptFlow = async () => {
    setLoading1(true);
    const generatedPrompt = await generateScript(formData);
    const scriptResult = await GenerateQuestions(generatedPrompt, formData.trainingId);
    setScript(scriptResult.data.questionResponse);
    setLoading1(false);
    setPromptDialogOpen(true);
  };

  const isUpdate = Boolean(formData.trainingId);
  const title = isUpdate ? 'Update Training' : 'Add Training';
  const selectedCourseName = formData.courseId
    ? courses.find((c) => c.id === formData.courseId)?.name ?? ''
    : '';

  if (!open) {
    return (
      <Promptdialog
        open={isPromptDialogOpen}
        handleClose={() => setPromptDialogOpen(false)}
        prompt={script}
        formData={formData}
      />
    );
  }

  return (
    <>
      <Promptdialog
        open={isPromptDialogOpen}
        handleClose={() => setPromptDialogOpen(false)}
        prompt={script}
        formData={formData}
      />
      <ModalShell
        title={title}
        onClose={handleClose}
        max="720px"
        foot={(
          <FooterWrap>
            {loading1 && (
              <DisclaimerCard style={{ flex: '1 1 100%', marginRight: 'auto' }}>
                <strong>Disclaimer:</strong>{' '}
                StudyakAI is a content creation tool. You are responsible for ensuring the accuracy
                and appropriateness of the script in this training based on the listed domains and
                subdomains.
              </DisclaimerCard>
            )}
            {formData.trainingId && aiBuilderEnabled && (
              <Btn
                type="button"
                $variant="ghost"
                onClick={handleGenerateScript}
                disabled={loading1}
              >
                {loading1 ? (
                  <ButtonSpinnerSlot size={16} />
                ) : (
                  script ? 'Show Script' : 'Generate Script'
                )}
              </Btn>
            )}
            <Btn type="button" $variant="ghost" onClick={handleClose}>
              Cancel
            </Btn>
            <Btn type="button" $variant="subtle" onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <ButtonSpinnerSlot size={16} />
              ) : (
                'Save'
              )}
            </Btn>
          </FooterWrap>
        )}
      >
        <FormRoot>
          <ToggleRow>
            <ToggleCopy>
              <ToggleTitle><LabelWithHelp help={HELP.scormMode}>{formData.isScrom === 1 ? 'SCORM training' : 'Standard training'}</LabelWithHelp></ToggleTitle>
              <ToggleHint>
                {formData.isScrom === 1
                  ? 'SCORM package mode — fewer generation fields.'
                  : 'Full AI script generation with character and video settings.'}
              </ToggleHint>
            </ToggleCopy>
            <Toggle
              checked={formData.isScrom === 1}
              onChange={handleScormToggle}
              label={formData.isScrom === 1 ? 'SCORM' : 'Training'}
            />
          </ToggleRow>

          <GridField>
            <label htmlFor="trainingName">Training name</label>
            <Input
              id="trainingName"
              name="trainingName"
              value={formData.trainingName}
              onChange={handleInputChange}
            />
          </GridField>

          <TwoColGrid>
            <GridField>
              <label htmlFor="courseName">Course name</label>
              <Select
                id="courseName"
                value={selectedCourseName}
                onChange={handleCourseChange}
                disabled={coursesLoading}
                required
              >
                <option value="">{coursesLoading ? 'Loading courses…' : 'Select a course'}</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.name}>
                    {course.name}
                  </option>
                ))}
              </Select>
            </GridField>

            {formData.trainingId ? (
              <>
                <GridField>
                  <label htmlFor="domains-readonly"><LabelWithHelp help={HELP.domains}>Domains</LabelWithHelp></label>
                  <Input
                    id="domains-readonly"
                    readOnly
                    value={formData.domains.map((domains) => domains.name).join(', ')}
                  />
                </GridField>
                <GridField>
                  <label htmlFor="subdomains-readonly"><LabelWithHelp help={HELP.subdomains}>Subdomains</LabelWithHelp></label>
                  <Input
                    id="subdomains-readonly"
                    readOnly
                    value={formData.domains
                      .flatMap((domains) => domains.subDomain.map((subDomain) => subDomain.name))
                      .join(', ')}
                  />
                </GridField>
              </>
            ) : (
              <>
                <GridField>
                  <label htmlFor="domain-select"><LabelWithHelp help={HELP.domains}>Domains</LabelWithHelp></label>
                  <MuiSelectWrap variant="outlined" required>
                    <MuiSelect
                      labelId="domain-select-label"
                      id="domain-select"
                      multiple
                      displayEmpty
                      value={formData.domains.map((domains) => domains.domainId)}
                      onChange={handleDomainSelectChange}
                      MenuProps={{ sx: { zIndex: MODAL_SCRIM_Z_INDEX + 10 } }}
                      input={<OutlinedInput id="select-multiple-chip" sx={muiSelectSx} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {selected.map((domainId) => (
                            <Tag key={domainId} $tone="accent">
                              {courseDetails.domains.find((domains) => domains.domainId === domainId)?.name}
                            </Tag>
                          ))}
                        </Box>
                      )}
                      sx={muiSelectSx}
                    >
                      {courseDetails.domains.map((domains) => (
                        <MenuItem key={domains.domainId} value={domains.domainId}>
                          {domains.name}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  </MuiSelectWrap>
                </GridField>

                <GridField>
                  <label htmlFor="subdomain-select"><LabelWithHelp help={HELP.subdomains}>Subdomains</LabelWithHelp></label>
                  <MuiSelectWrap variant="outlined" required>
                    <MuiSelect
                      labelId="subdomain-select-label"
                      id="subdomain-select"
                      multiple
                      displayEmpty
                      value={formData.domains.flatMap((domains) =>
                        domains.subDomain.map((subDomain) => subDomain.subDomainId),
                      )}
                      onChange={handleSubDomainSelectChange}
                      MenuProps={{ sx: { zIndex: MODAL_SCRIM_Z_INDEX + 10 } }}
                      input={<OutlinedInput id="select-multiple-subdomain" sx={muiSelectSx} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {selected.map((subDomainId) => (
                            <Tag key={subDomainId} $tone="accent">
                              {courseDetails.domains
                                .flatMap((domains) => domains.subDomain)
                                .find((subDomain) => subDomain.subDomainId === subDomainId)?.name}
                            </Tag>
                          ))}
                        </Box>
                      )}
                      sx={muiSelectSx}
                    >
                      {courseDetails.domains
                        .flatMap((domains) => domains.subDomain)
                        .map((subDomain) => (
                          <MenuItem key={subDomain.subDomainId} value={subDomain.subDomainId}>
                            {subDomain.name}
                          </MenuItem>
                        ))}
                    </MuiSelect>
                  </MuiSelectWrap>
                </GridField>
              </>
            )}

            {formData.isScrom == 1 && (
              <GridField>
                <label htmlFor="questionsPerVideo"><LabelWithHelp help={HELP.questionsPerVideo}>Number of questions</LabelWithHelp></label>
                <Input
                  id="questionsPerVideo"
                  name="questionsPerVideo"
                  type="number"
                  value={formData.questionsPerVideo}
                  onChange={handleInputChange}
                />
              </GridField>
            )}

            {formData.isScrom !== 1 && (
              <>
                <GridField>
                  <label htmlFor="numberOfCharacters"><LabelWithHelp help={HELP.numberOfCharacters}>Number of characters</LabelWithHelp></label>
                  <Input
                    id="numberOfCharacters"
                    name="numberOfCharacters"
                    type="number"
                    value={formData.numberOfCharacters}
                    onChange={handleInputChange}
                  />
                </GridField>

                <GridField>
                  <label htmlFor="gender"><LabelWithHelp help={HELP.gender}>Gender</LabelWithHelp></label>
                  <Select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                  >
                    <option value="">Select gender</option>
                    {genderOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </GridField>

                <GridField>
                  <label htmlFor="race"><LabelWithHelp help={HELP.race}>Race</LabelWithHelp></label>
                  <Select
                    id="race"
                    name="race"
                    value={formData.race}
                    onChange={handleInputChange}
                  >
                    <option value="">Select race</option>
                    {raceOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </GridField>

                <GridField>
                  <label htmlFor="language"><LabelWithHelp help={HELP.language}>Language</LabelWithHelp></label>
                  <Select
                    id="language"
                    name="language"
                    value={formData.language}
                    onChange={handleInputChange}
                  >
                    <option value="">Select language</option>
                    {languageOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </GridField>

                <GridField>
                  <label htmlFor="videoLength"><LabelWithHelp help={HELP.videoLength}>Video length (minutes)</LabelWithHelp></label>
                  <Input
                    id="videoLength"
                    name="videoLength"
                    type="number"
                    value={formData.videoLength}
                    onChange={handleInputChange}
                  />
                </GridField>

                <GridField>
                  <label htmlFor="questionsPerVideo-training"><LabelWithHelp help={HELP.questionsPerVideo}>Questions per video</LabelWithHelp></label>
                  <Input
                    id="questionsPerVideo-training"
                    name="questionsPerVideo"
                    type="number"
                    value={formData.questionsPerVideo}
                    onChange={handleInputChange}
                  />
                </GridField>

                <GridField style={{ gridColumn: '1 / -1' }}>
                  <ToggleRow>
                    <ToggleCopy>
                      <ToggleTitle><LabelWithHelp help={HELP.createFinalExam}>Create final exam</LabelWithHelp></ToggleTitle>
                      <ToggleHint>Generate a final assessment when saving this training.</ToggleHint>
                    </ToggleCopy>
                    <Toggle
                      checked={formData.createFinalExam === 1}
                      onChange={(v) =>
                        setFormData({ ...formData, createFinalExam: v ? 1 : 0 })
                      }
                      label={formData.createFinalExam === 1 ? 'Enabled' : 'Disabled'}
                    />
                  </ToggleRow>
                </GridField>
              </>
            )}
          </TwoColGrid>
        </FormRoot>
      </ModalShell>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
};

export default FormDialog;
